import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useMutation } from '@tanstack/react-query';
import { useState } from 'react';
import { AlertTriangle, KeyRound } from 'lucide-react';
import { api, ApiError } from '@/api/client';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { PublicShell } from '@/components/PublicShell';

export const Route = createFileRoute('/login/recovery-code')({
  component: LoginRecoveryCodePage,
});

const schema = z.object({ recoveryCode: z.string().min(8, 'Recovery code required') });
type Values = z.infer<typeof schema>;

function LoginRecoveryCodePage() {
  const navigate = useNavigate();
  const [error, setError] = useState<string | null>(null);
  const form = useForm<Values>({
    resolver: zodResolver(schema),
    defaultValues: { recoveryCode: '' },
  });

  const submit = useMutation({
    mutationFn: (v: Values) => api('/auth/login-with-recovery-code', { method: 'POST', json: v }),
    onSuccess: () => navigate({ to: '/returns' }),
    onError: (err) => {
      if (err instanceof ApiError) {
        const problem = err.problem as { title?: string } | null;
        setError(problem?.title ?? 'Invalid recovery code');
      } else setError('Could not reach server');
    },
  });

  return (
    <PublicShell>
      <div className="mx-auto max-w-xl px-6 py-16">
        <div className="section-ref">Recovery code</div>
        <h1 className="mt-3 font-serif text-4xl font-medium tracking-tight">
          Use a one-time recovery code
        </h1>
        <div className="mt-3 gov-rule" aria-hidden />
        <p className="mt-6 text-[15px] leading-relaxed text-(--color-ink-2)">
          Enter one of the recovery codes you saved when you set up two-factor authentication.
          Each code works once. After you sign in, regenerate the rest from{' '}
          <span className="font-medium">Account → Two-factor</span>.
        </p>

        <Card className="mt-8">
          <CardContent className="p-8">
            <form
              onSubmit={form.handleSubmit((v) => {
                setError(null);
                submit.mutate(v);
              })}
              className="space-y-5"
            >
              <div className="space-y-2">
                <Label htmlFor="rc-code" required>
                  Recovery code
                </Label>
                <Input
                  id="rc-code"
                  autoComplete="off"
                  className="font-mono tracking-[0.2em]"
                  aria-invalid={!!form.formState.errors.recoveryCode || undefined}
                  {...form.register('recoveryCode')}
                />
                {form.formState.errors.recoveryCode && (
                  <p className="flex items-center gap-1.5 text-[12px] font-medium text-(--color-maroon)">
                    <AlertTriangle className="size-3.5" />
                    {form.formState.errors.recoveryCode.message}
                  </p>
                )}
              </div>
              {error && (
                <p className="flex items-start gap-2.5 rounded-(--radius-sm) border-l-[3px] border-(--color-maroon) bg-(--color-maroon-soft) px-3 py-2.5 text-[13px] font-medium text-(--color-maroon)">
                  <AlertTriangle className="mt-0.5 size-4 shrink-0" />
                  {error}
                </p>
              )}
              <Button type="submit" className="w-full" size="lg" disabled={submit.isPending}>
                <KeyRound className="size-4" />
                {submit.isPending ? 'Verifying…' : 'Sign in with recovery code'}
              </Button>
            </form>
          </CardContent>
        </Card>

        <p className="mt-6 text-center text-[14px] text-(--color-ink-2)">
          <Link to="/login/2fa" className="gov-link font-medium">
            Back to authenticator code
          </Link>
        </p>
      </div>
    </PublicShell>
  );
}
